import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button, Form, Modal } from "react-bootstrap";
import { sourcingAnalysis, createRun, setRunStatus, listRequisitions, draftRequisitions, draftRunRequisitions } from "../../services/sourcingService";
import { approveRequisition, cancelRequisition } from "../../services/vendorInboxService";
import { errorToast, successToast } from "../Toasts/Toasts";

// The desk's sourcing view for one requirement (MG, 22 Sep 2026): which
// lane each costed line ships from, what the trip costs against the goods,
// whether it is worth running alone or should be clubbed with another open
// requirement on the same lane - and the asks that go out to vendors for
// the lines that still have no usable rate.

const inr = (n) => (n === null || n === undefined ? "—" : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`);
const when = (d) => (d ? new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
const REQ_CLASS = { Draft: "secondary", Sent: "primary", Replied: "success", Failed: "danger", Cancelled: "light text-dark" };

const SourcingPanel = ({ number, requirementId, onChanged }) => {
  const [a, setA] = useState(null);
  const [asks, setAsks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [club, setClub] = useState(null); // { lane, picked: Set }
  const [preview, setPreview] = useState(null); // requisition being approved

  const load = useCallback(async () => {
    try {
      const [analysis, reqs] = await Promise.all([sourcingAnalysis(number), listRequisitions(number)]);
      setA(analysis);
      setAsks(reqs);
    } catch (e) { errorToast(e?.message || "Could not load sourcing"); }
  }, [number]);
  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onChanged && onChanged(); };

  const ask = async () => {
    setBusy(true);
    try {
      const r = a?.run ? await draftRunRequisitions(a.run.id) : await draftRequisitions(number);
      if (!r?.success) return errorToast(r?.message);
      const { drafted, unassignedLines, reason } = r.data;
      if (drafted.length) successToast(`${drafted.length} ask(s) drafted - approve each to send`);
      else errorToast(reason || "Nothing to ask");
      if (unassignedLines?.length) errorToast(`No vendor history for: ${unassignedLines.map((u) => u.item).join("; ")}`);
      refresh();
    } catch (e) { errorToast(e?.message); } finally { setBusy(false); }
  };

  const approve = async (id, messageText) => {
    setBusy(true);
    try { const r = await approveRequisition(id, messageText); if (r) { successToast(`Sent to ${r.vendorName}${r.via === "template" ? " (template - outside the 24 h window)" : ""}`); setPreview(null); refresh(); } }
    catch (e) { errorToast(e?.message); } finally { setBusy(false); }
  };
  const cancel = async (id) => {
    try { await cancelRequisition(id); refresh(); } catch (e) { errorToast(e?.message); }
  };

  const makeRun = async () => {
    setBusy(true);
    try {
      const ids = [requirementId, ...club.picked];
      const r = await createRun({ laneCity: club.lane.city, requirementIds: ids });
      if (r?.success) { successToast(`Run #${r.data.id} on ${r.data.laneCity}: ${r.data.requirements.map((x) => x.number).join(", ")}`); setClub(null); refresh(); } else errorToast(r?.message);
    } catch (e) { errorToast(e?.message); } finally { setBusy(false); }
  };
  const leaveRun = async () => {
    if (!window.confirm("Cancel this sourcing run? Each requirement carries its full transport again.")) return;
    try { const r = await setRunStatus(a.run.id, "Cancelled"); if (r?.success) refresh(); else errorToast(r?.message); } catch (e) { errorToast(e?.message); }
  };

  if (!a) return null;
  const open = asks.filter((r) => r.status !== "Cancelled");

  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="fw-bold mb-0">Sourcing</h6>
          <div className="d-flex gap-2 align-items-center">
            <Link className="small" to="/catalogue/vendors">lanes</Link>
            <Button size="sm" variant="outline-success" disabled={busy} onClick={ask}>Ask vendors</Button>
          </div>
        </div>

        {a.run && (
          <div className="alert alert-info py-2 small mb-2 d-flex justify-content-between align-items-center">
            <div>
              In run <b>#{a.run.id}</b> · {a.run.laneCity} · {a.run.status} · {a.run.requirements.map((r) => r.id === requirementId ? <b key={r.id}>{r.number}</b> : <Link key={r.id} to={`/requirements/${r.number}`} className="me-1">{r.number}</Link>)}
              <div>goods {inr(a.run.goodsEstimate)} · transport {inr(a.run.transportTotal)}{a.run.transportPct !== null ? ` (${a.run.transportPct}%)` : ""} shared by goods value</div>
            </div>
            {a.run.status !== "Closed" && <Button size="sm" variant="link" className="text-danger p-0" onClick={leaveRun}>cancel run</Button>}
          </div>
        )}

        {a.lanes.length ? (
          <table className="table table-sm mb-2 align-middle">
            <thead><tr><th>Lane</th><th>Vendors</th><th className="text-end">Goods</th><th className="text-end">Transport</th><th className="text-end">Share</th><th>Verdict</th><th></th></tr></thead>
            <tbody>
              {a.lanes.map((l) => (
                <tr key={l.city} className={l.heavy ? "table-warning" : ""}>
                  <td>{l.city}<div className="small text-muted">{l.lineCount} line(s) · {l.units} units</div></td>
                  <td className="small">{l.vendors.join(", ")}</td>
                  <td className="text-end">{inr(l.goods)}</td>
                  <td className="text-end">{l.laneKnown ? <>{inr(l.transport)}{l.transportShare < 1 ? <div className="small text-muted">of {inr(l.minTransport)}</div> : null}<div className="small text-muted">{inr(l.perUnit)}/unit</div></> : <span className="text-danger">not set</span>}</td>
                  <td className="text-end">{l.transportPct !== null ? `${l.transportPct}%` : "—"}</td>
                  <td className="small">
                    {l.verdict}
                    {l.clubWith?.length > 0 && !a.run && <div className="text-muted">with {l.clubWith.map((c) => `${c.number} (${inr(c.goods)})`).join(", ")}: {l.combinedPct}%</div>}
                  </td>
                  <td className="text-end">{l.clubWith?.length > 0 && !a.run && <Button size="sm" variant="outline-primary" onClick={() => setClub({ lane: l, picked: new Set(l.clubWith.map((c) => c.requirementId)) })}>Club</Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <div className="small text-muted mb-2">No costed lines yet - lanes appear once a line has a vendor rate.</div>}
        <div className="small text-muted mb-3">Transport above {a.heavyPct}% of goods is "too small to run alone". Per-unit share is already inside each line's landed cost.</div>

        <h6 className="fw-bold small mb-1">Asks to vendors</h6>
        {open.length ? open.map((r) => (
          <div key={r.id} className="border-top py-2 d-flex justify-content-between align-items-start gap-2">
            <div className="small">
              <span className={`badge bg-${REQ_CLASS[r.status] || "secondary"} me-1`}>{r.status}</span>
              <Link to={`/vendor-inbox?vendor=${r.zohoVendorId}`}>{r.vendorName}</Link>{r.sourcingRunId ? <span className="text-muted"> · run #{r.sourcingRunId}</span> : null}{r.sentAt ? <span className="text-muted"> · {when(r.sentAt)}</span> : null}
              <div className="text-muted">{r.lines.map((l) => `${l.zohoVariantName || l.zohoProductName} ×${Number(l.quantity || 0)}${l.status === "Answered" ? " ✓" : ""}`).join(" · ")}</div>
              {r.error && <div className="text-danger">{r.error}</div>}
            </div>
            <div className="d-flex gap-1">
              {(r.status === "Draft" || r.status === "Failed") && <Button size="sm" variant="success" disabled={busy} onClick={() => setPreview({ ...r, text: r.messageText })}>Review & send</Button>}
              {r.status === "Draft" && <Button size="sm" variant="outline-secondary" onClick={() => cancel(r.id)}>Drop</Button>}
            </div>
          </div>
        )) : <div className="small text-muted">Nothing asked yet. "Ask vendors" drafts one message per vendor for the lines with no usable rate - the best-behaved vendors with history first.</div>}
      </div>

      <Modal show={!!preview} onHide={() => setPreview(null)}>
        <Modal.Header closeButton><Modal.Title>Send to {preview?.vendorName}</Modal.Title></Modal.Header>
        {preview && <Modal.Body>
          <Form.Control as="textarea" rows={6} value={preview.text} onChange={(e) => setPreview({ ...preview, text: e.target.value })} />
          <div className="small text-muted mt-2">Goes from the vendor number as a WhatsApp text; if the vendor's 24-hour window is closed it goes inside the generic query template instead.</div>
        </Modal.Body>}
        <Modal.Footer><Button variant="success" disabled={busy} onClick={() => approve(preview.id, preview.text !== preview.messageText ? preview.text : undefined)}>Send</Button></Modal.Footer>
      </Modal>

      <Modal show={!!club} onHide={() => setClub(null)}>
        <Modal.Header closeButton><Modal.Title>Club on {club?.lane.city}</Modal.Title></Modal.Header>
        {club && <Modal.Body>
          <div className="small mb-2">One trip ({inr(club.lane.minTransport)}) shared across these requirements by goods value. Each requirement's landed cost drops accordingly.</div>
          {club.lane.clubWith.map((c) => {
            const key = c.requirementId;
            return <Form.Check key={key} type="checkbox" id={`club-${key}`} label={`${c.number} · ${c.accountName} · ${inr(c.goods)}${c.deliveryDate ? ` · by ${c.deliveryDate}` : ""}`} checked={club.picked.has(key)} onChange={(e) => { const p = new Set(club.picked); e.target.checked ? p.add(key) : p.delete(key); setClub({ ...club, picked: p }); }} />;
          })}
        </Modal.Body>}
        <Modal.Footer><Button variant="primary" disabled={busy || !club?.picked.size} onClick={makeRun}>Create run</Button></Modal.Footer>
      </Modal>
    </div>
  );
};

export default SourcingPanel;
